import { BookingAvailability, QuestionStatus } from './bff/gql-schema';
import { Nullable } from './common';
import { AttachmentBlindType } from './community';
import { MagazineBottomButtonType } from './magazine';
import { QuestionPaymentTotal } from './qna';

export enum EAuthorType {
  Ai = 'AI',
  Doctor = 'DOCTOR',
  GeneralEditor = 'GENERAL_EDITOR',
  MedicalProfessionalEditor = 'MEDICAL_PROFESSIONAL_EDITOR',
  Nurse = 'NURSE',
}

export enum EQuestionAnsweredStatus {
  Answered = 'ANSWERED',
  Answering = 'ANSWERING',
  Checking = 'CHECKING',
}

export enum EProfessionalFieldType {
  Department = 'DEPARTMENT',
}

export enum EQuestionAccessibleType {
  All = 'ALL',
  DoctorsAndMe = 'DOCTORS_AND_ME',
  Me = 'ME',
}

export enum EAnswererType {
  Ai = 'AI',
  Doctor = 'DOCTOR',
  Nurse = 'NURSE',
}

export enum EAnswerLikeType {
  Dislike = 'DISLIKE',
  Like = 'LIKE',
}

export enum EIntegratedSearchTabType {
  All = 'ALL',
  Qna = 'QNA',
  Magazine = 'MAGAZINE',
  Doctor = 'DOCTOR',
  Hospital = 'HOSPITAL',
  MedicineDictionary = 'MEDICINE_DICTIONARY',
  DiseaseEncyclopedia = 'DISEASE_ENCYCLOPEDIA',
}

export enum ERequestButtonType {
  BOTTOM = 'bottom',
  FLOATING = 'floating',
}

export type HomeSearchKeywordList = Array<string>;

export interface RequestInfoCategory {
  readonly id: number;
  readonly name: string;
  readonly isNeedToUserDirectInput: boolean;
}
export interface ProfessionalDepartment {
  readonly doctorId: number;
  readonly professionalDepartmentId: number;
  readonly professionalDepartmentName: string;
}

export interface DiagnosisCount {
  readonly doctorId: number;
  readonly count: number;
}

export interface DoctorSearchResultVo {
  readonly doctorId: number;
  readonly profileUrl: string;
  readonly doctorName: string;
  readonly hospitalName: string;
  readonly reviewCount: number; // 리뷰 수
  readonly reviewAverageRating: number; // 리뷰 평점
  readonly isAbsent: boolean; // 자리비움 여부
  readonly isBookable: boolean; // 예약 가능 여부
  readonly readyForVideoCall: boolean; // 화상진료 여부
  readonly professionalDepartment: ProfessionalDepartment; // 전문의 과목
  readonly diagnosisCount: DiagnosisCount; // 대기중인 진료 카운트
}

export interface BoardSearchResultVo {
  readonly boardPostPid: string;
  readonly boardCategoryId: string;
  readonly title: string;
  readonly content: string;
  readonly likeCount: number;
  readonly boardCommentCount: number;
  readonly createdAt: number;
  readonly isLikedByMe: boolean;
  readonly isMine: boolean;
  readonly blinded: boolean;
  readonly boardPostAttachments: Array<{
    readonly boardPostAttachmentId: string;
    readonly blindType: AttachmentBlindType;
  }>;
  readonly communityUser: {
    readonly nickname: string;
    readonly userId: string;
    readonly profileImageUrl: string;
  };
  readonly boardCategory: { name: string };
  readonly boardComments?: Array<{
    readonly boardCommentPid: string;
    readonly parentBoardCommentPid: string;
    readonly boardPostPid: string;
    readonly content: string;
    readonly createdAt: number;
    readonly deleted: boolean;
    readonly isLikedByMe: boolean;
    readonly isMine: boolean;
    readonly likeCount: number;
    readonly blinded: boolean;
    readonly communityUser: {
      readonly nickname: string;
      readonly userId: string;
      readonly profileImageUrl: string;
    };
  }>;
  readonly formattedCreatedAt: string;
  readonly tags: Array<{
    readonly type: string;
    readonly label: string;
  }>;
}

export interface MagazineSearchResultVo {
  readonly content: string;
  readonly mobileContent: string;
  readonly createdAt: number;
  readonly description: string;
  readonly likeCount: number;
  readonly magazinePid: string;
  readonly questionCategoryId: string;
  readonly title: string;
  readonly mobileTitle: string;
  readonly mobileDescription: string;
  readonly titleImageUrl: string;
  readonly tagNames: Array<string>;
  readonly footnote: string;
  readonly isLikedByMe: boolean;
  readonly bottomButtonType: MagazineBottomButtonType;
  readonly author?: {
    readonly affiliatedInstitutionName: string;
    readonly authorId: string;
    readonly authorType: EAuthorType;
    readonly externalLinkUrl?: string | null;
    readonly name: string;
    readonly profileImageUrl: string;
    readonly professionalFields: Array<{ name: string }>;
  } | null;
  readonly createdDate: string;
}

export interface QuestionSearchResultVo {
  readonly question: {
    readonly status?: QuestionStatus | null;
    readonly accessibleType: EQuestionAccessibleType;
    readonly content: string;
    readonly createdAt: number;
    readonly isBlockedByMe: boolean;
    readonly isMine: boolean;
    readonly likeCount: number;
    readonly questionPid: string;
    readonly title: string;
    readonly attachments: Array<{
      readonly blindType: AttachmentBlindType;
      readonly questionAttachmentId: string;
    }>;
    readonly category: { readonly name: string; readonly iconImageUrl: string };
    readonly tags: Array<{ readonly name: string }>;
  };
  readonly answer?: {
    readonly answerPid: string;
    readonly content: string;
    readonly createdAt: number;
    readonly dislikeCount: number;
    readonly footer?: string | null;
    readonly header?: string | null;
    readonly isBlockedByUser: boolean;
    readonly likeCount: number;
    readonly myLikeType?: EAnswerLikeType | null;
    readonly answerer: {
      readonly affiliatedInstitutionName: string;
      readonly answererId: string;
      readonly answererType: EAnswererType;
      readonly name: string;
      readonly profileImageUrl: string;
      readonly professionalFields: Array<{
        readonly name: string;
        readonly professionalFieldId: string;
        readonly professionalFieldType: EProfessionalFieldType;
      }>;
    };
  } | null;
  readonly questionCreatedAt: string;
  readonly answerCreatedAt: string | null;
}
export interface PopularSymptomVo {
  readonly popularSymptomList: Array<{
    symptomName: string;
    symptomPublicId: string;
  }>;
}

export interface KeywordSearchMagazineVo {
  readonly createdAt: number;
  readonly magazinePid: string;
  readonly title: string;
  readonly description: string;
  readonly titleImageUrl: string;
  readonly mobileTitle: string;
  readonly mobileDescription: string;
  readonly content: string;
  readonly author?: {
    readonly affiliatedInstitutionName: string;
    readonly authorId: string;
    readonly authorType: EAuthorType;
    readonly externalLinkUrl?: string | null;
    readonly name: string;
    readonly profileImageUrl: string;
    readonly professionalFields: Array<{ name: string }>;
  } | null;
  readonly createdDate: string;
}

export interface KeywordSearchQuestionVo {
  readonly answer?: {
    readonly answerPid: string;
    readonly content: string;
    readonly createdAt: number;
    readonly dislikeCount: number;
    readonly footer?: string | null;
    readonly header?: string | null;
    readonly isBlockedByUser: boolean;
    readonly likeCount: number;
    readonly myLikeType?: EAnswerLikeType | null;
    readonly answerer: {
      readonly affiliatedInstitutionName: string;
      readonly answererId: string;
      readonly answererType: EAnswererType | undefined;
      readonly name: string;
      readonly profileImageUrl: string;
      readonly professionalFields: Array<{
        readonly name: string;
        readonly professionalFieldId: string;
        readonly professionalFieldType: EProfessionalFieldType;
      }>;
    };
  } | null;
  readonly question: {
    readonly status?: QuestionStatus | null;
    readonly accessibleType: EQuestionAccessibleType;
    readonly content: string;
    readonly createdAt: number;
    readonly isBlockedByMe: boolean;
    readonly isLikedByMe: boolean;
    readonly isMine: boolean;
    readonly likeCount: number;
    readonly questionPid: string;
    readonly title: string;
    readonly attachments: Array<{
      readonly blindType: AttachmentBlindType;
      readonly questionAttachmentId: string;
    }>;
    readonly category: {
      name: string;
      iconImageUrl: string;
      questionCategoryId: string;
    };
    readonly tags: Array<{ name: string }>;
    readonly user: { name: string; userId: string };
  };
  readonly questionCreatedAt: string;
  readonly answerCreatedAt: string | null;
  readonly paymentTotal?: Nullable<QuestionPaymentTotal>;
}
export type SearchResult<T> = {
  totalPage?: number;
  totalCount?: number;
  elements: Array<T>;
  hasMore: boolean;
};

export interface NonF2fDoctorVo {
  createdAt: number;
  distance?: number | null;
  id: string;
  publicId: string;
  isAffiliated: boolean;
  isNew: boolean;
  medicineType: string;
  name: string;
  profileUrl?: string | null;
  readyForVideoCall: boolean;
  reviewAverageRating: number;
  reviewCount: number;
  departments: Array<{
    id: string;
    description?: string | null;
    isProfessional: boolean;
    name: string;
  }>;
  nonBenefitMedicinePricing?: {
    doctorId: string;
    doctorTreatmentMedicineUnitPricingId: string;
    originalPrice: number;
    priceOnUsingPoint: number;
    treatmentMedicineName: string;
    unitDisplayName: string;
    unitTypeDisplayName: string;
    usablePoint: number;
  } | null;
  professionalDepartment?: {
    description?: string | null;
    id: string;
    isProfessional: boolean;
    name: string;
  } | null;
  diagnosisCount?: { count?: number | null; doctorId: string } | null;
  hospitalV2: { doctorHospitalId: string; doctorId: string; hospitalName: string };
  bookingInfo: {
    bookingAvailability: BookingAvailability;
    isBookable: boolean;
    todayBookable: boolean;
    todayCutoffTime: string;
    todayKickoffTime: string;
    todaySchedule: string;
    tomorrowBookable: boolean;
    tomorrowCutoffTime: string;
    tomorrowKickoffTime: string;
    tomorrowSchedule: string;
  };
}

export enum EGeneralSearchSnippetType {
  DeliveryGuide = 'DELIVERY_GUIDE',
  FaceToFaceDiagnosis = 'FACE_TO_FACE_DIAGNOSIS',
  FaceToFacePrescription = 'FACE_TO_FACE_PRESCRIPTION',
  NonFaceToFaceDiagnosis = 'NON_FACE_TO_FACE_DIAGNOSIS',
  NonFaceToFacePrescription = 'NON_FACE_TO_FACE_PRESCRIPTION',
}

export enum EGeneralSearchFeatureType {
  DietInjection = 'DIET_INJECTION',
  EmergencyContraceptive = 'EMERGENCY_CONTRACEPTIVE',
  FindPharmacy = 'FIND_PHARMACY',
}

export interface GeneralSearchMetaByKeywordVo {
  keyword: string;
  snippet?: EGeneralSearchSnippetType;
  features?: Array<EGeneralSearchFeatureType>;
}
